/**
 * The building noticing that you found the way down.
 *
 * A slide is the one exit the player is meant to hunt for, and finding one
 * should be the best and the worst thing that happens on a floor. This is the
 * worst half. Standing near the mouth of a slide makes the level steadily less
 * survivable until the player goes through it, so the way down is a decision
 * taken at a run rather than a landmark to loiter at.
 *
 * Everything here is inert until the player is within `ARM_RADIUS` metres of a
 * slide. A floor crossed without meeting one — or a floor with no slide on it
 * at all — runs at exactly the rate `director.ts` describes, with the same long
 * quiet stretches and the same fakeout-to-spawn ratio. That is deliberate: the
 * surge is a burst tied to one place, not a new baseline.
 *
 * ## Two radii
 *
 * The reach (`armRadius`, 56 m) and the escalation (`dwellRadius`, 20 m) are
 * different distances doing different jobs, and conflating them was the bug
 * this file was written around. The reach is where the *sequence* starts: it is
 * sized so that a player walking in still has ground to cover while the thing
 * that was summoned crosses the floor toward them, and inside it a spawn is
 * narrated as a hunt. The escalation is where the *price* starts: proximity
 * drives the pressure multiplier, the grace burn, the lure and the cue track's
 * intensity, and it is deliberately small, because a fifty-six metre circle of
 * multiplied spawn rate around every mouth would make most of a floor a surge.
 *
 * ## The approach window
 *
 * A multiplied rate cannot promise anything, and the slide moment needs a
 * promise: measured, a player walking in from sixty metres had a monster in
 * view before they reached the mouth in 2 runs out of 24. So heading for a
 * mouth — `approach.commit` metres of ground genuinely given up toward one —
 * opens a window, and the window buys one hunter outright: a lump of pressure
 * that crosses the director's threshold in a couple of seconds, and a fakeout
 * chance of nearly zero on the roll it pays for. One hunt per window, one
 * window per `approach.cooldown`, and nothing at all for somebody who merely
 * passes within reach on their way somewhere else.
 *
 * What it raises is the *rate*, and once per window the certainty. Spawn
 * placement, the out-of-sight rule and "nothing accrues while something is
 * already happening" all still belong to the director and are untouched, so a
 * surge can never put a hound in the player's face and can never open a window
 * on top of an encounter that is already running. What it buys is pressure per
 * second, a bias toward fakeouts early and real spawns late, a bed of noise
 * that closes in, and — once the player has been standing there long enough to
 * have earned it — whatever is already on the floor being pointed at them.
 *
 * Everything it does is still scaled by the level's `threatScale`: it buys the
 * window's pressure and it divides the wait for the next one, so the Poolrooms
 * gets one slow thing crossing the water when the player finds the way down and
 * Level 2 gets three times as many.
 *
 * It is also a loan. Pressure the surge adds is remembered and handed back once
 * the player leaves, because a player who looks at a slide, thinks better of it
 * and walks away must not carry a fully charged director around the rest of the
 * floor with them.
 *
 * ## The long hunt
 *
 * The one thing the surge does change about placement is distance. A spawn it
 * pays for is put most of the way across the floor rather than in the ordinary
 * band, and then the surge spends the next twenty-odd seconds making the
 * approach audible: a noise on the hunter's true bearing, closer and faster and
 * lower each time, so the player hears something a long way off, works out
 * which way it is coming from, and has to decide whether to run for the slide
 * or go quiet and let it pass.
 *
 * The two halves of that are `beginHunt` — which licenses the thing to stay
 * awake across a distance the sleep rule would otherwise cancel it at — and the
 * trail, which is the building telling it roughly where the player was heard.
 * The trail is gated on the player's own noise and is deliberately vague at
 * range, so the counterplay the hound is built around survives intact: stop
 * moving and the building stops pointing, and the last sixteen metres have to
 * be earned by the hunter's own ears — which is a distance chosen because it is
 * inside those ears for a player who is walking and outside them for anyone
 * quieter. See `hunt.trailHandover`.
 */

import { TUNING } from '../core/config';
import { clamp01, lerp, smoothstep } from '../core/math';
import { makeRNG } from '../core/rng';
import type { GeneratedLevel, RNG, SfxName, Vec2 } from '../core/types';
import { randomWalkableNear, type Agent, type EntityWorld } from './brain';

/**
 * Every number here is documented in `TUNING.director.surge`, beside the
 * director numbers it multiplies. Destructured once so the model below reads as
 * the model rather than as a walk through a config object.
 */
const {
  armRadius: ARM_RADIUS,
  armHysteresis: ARM_HYSTERESIS,
  dwellRadius: DWELL_RADIUS,
  mouthRadius: MOUTH_RADIUS,
  rampFull: RAMP_FULL,
  baseScale: SURGE_BASE,
  peakScale: SURGE_PEAK,
  dwellDecay: DWELL_DECAY,
  drainRate: DRAIN_RATE,
  graceBurn: GRACE_BURN,
  fakeoutEarly: FAKEOUT_EARLY,
  fakeoutLate: FAKEOUT_LATE,
  cueSlow: CUE_SLOW,
  cueFast: CUE_FAST,
  cueFar: CUE_FAR,
  cueNear: CUE_NEAR,
  cueVoiceIntensity: CUE_VOICE_INTENSITY,
  cueAfterFakeout: CUE_AFTER_FAKEOUT,
  lureIntensity: LURE_INTENSITY,
  lureInterval: LURE_INTERVAL,
  lureLoudness: LURE_LOUDNESS,
  warnIntensity: WARN_INTENSITY,
  tensionCap: TENSION_CAP,
  intensityFloor: INTENSITY_FLOOR,
} = TUNING.director.surge;

/** The approach window's own numbers, documented in `TUNING.director.surge.approach`. */
const {
  commit: APPROACH_COMMIT,
  slack: APPROACH_SLACK,
  charge: APPROACH_CHARGE,
  window: APPROACH_WINDOW,
  cooldown: APPROACH_COOLDOWN,
  retry: APPROACH_RETRY,
  fakeout: APPROACH_FAKEOUT,
} = TUNING.director.surge.approach;

/** The long hunt's own numbers, documented in `TUNING.director.surge.hunt`. */
const {
  far: HUNT_FAR,
  near: HUNT_NEAR,
  handover: HUNT_HANDOVER,
  trailHandover: HUNT_TRAIL_HANDOVER,
  cueSlow: HUNT_CUE_SLOW,
  cueFast: HUNT_CUE_FAST,
  progressLicence: HUNT_PROGRESS_LICENCE,
  progressStep: HUNT_PROGRESS_STEP,
  trackFar: HUNT_TRACK_FAR,
  trackNear: HUNT_TRACK_NEAR,
  trailSlow: TRAIL_SLOW,
  trailFast: TRAIL_FAST,
  trailSpread: TRAIL_SPREAD,
  wakeLoudness: HUNT_WAKE_LOUDNESS,
} = TUNING.director.surge.hunt;

/**
 * Metres out to which ground given up toward a mouth is counted.
 *
 * Wider than the reach on purpose, and derived from it: a player who has been
 * walking at the way down since before they crossed into the band has already
 * made the case by the time they get there, so the window opens on the crossing
 * rather than `APPROACH_COMMIT` metres later — which is five seconds of the
 * lead the reach was sized to provide. Nothing else happens out here; the
 * window itself still may not open until the surge is armed.
 */
const APPROACH_WATCH = ARM_RADIUS + APPROACH_COMMIT + APPROACH_SLACK;

/** Bounds on how far a level's `threatScale` may stretch or shrink a hunt. */
const HUNT_SCALE_MIN = 0.4;
const HUNT_SCALE_MAX = 1.4;

/**
 * The same, for the approach. Wider at the bottom than `HUNT_SCALE_MIN`,
 * because this one divides the wait between windows and a floor as calm as
 * Level 94 should genuinely only be asked once; clamped off zero so the
 * arithmetic cannot run away on a level that is nearly harmless.
 */
const THREAT_SCALE_MIN = 0.2;
const THREAT_SCALE_MAX = 1.5;

/** Salt for the surge's own RNG stream. */
const SURGE_SALT = 0x51de;

/**
 * How much of the surge a level's identity lets through to the audio. Pressure
 * is already scaled by `threatScale` where the director applies it; this is the
 * *presentation*, floored so the Poolrooms still tell the player what is
 * happening and capped so a deep loop cannot exceed a full-strength floor.
 */
function audioMix(threatScale: number): number {
  return clamp01(0.25 + 0.75 * threatScale);
}

/** Sounds the surge draws on. The distant pool, closing in. */
const SURGE_SFX: readonly SfxName[] = ['distant-thud', 'distant-scrape'];
const SURGE_SFX_CLOSE: readonly SfxName[] = ['distant-thud', 'distant-scrape', 'distant-voice'];

/* ------------------------------------------------------------------ *
 * Contract
 * ------------------------------------------------------------------ */

/** What the surge is allowed to do to the world. Supplied by the director. */
export interface SurgeHost {
  /** Live agents, for the lure. */
  readonly agents: readonly Agent[];
  /**
   * A walkable point the player cannot see, `minR`..`maxR` metres away. The
   * director's own placement helper: the surge must not invent its own idea of
   * where a noise may come from.
   */
  offscreenSpot(w: EntityWorld, minR: number, maxR: number, behind: boolean): Vec2 | null;
}

/** The slide surge. One per director, reset on every level load. */
export interface SlideSurge {
  /**
   * Point it at a level. Reads the slides straight off `level.exits`.
   *
   * Takes a seed rather than a stream on purpose: forking one costs the parent
   * a draw, and the director's stream must run identically on a level where the
   * player never goes near a slide. This one is independent and deterministic.
   */
  setLevel(level: GeneratedLevel, seed: number): void;
  /**
   * Advance one frame. Must run before the director's grace check, since the
   * surge is what burns the grace period down.
   *
   * `live` is the director's own "am I accruing" state: false during the
   * opening grace period, and false while something is already engaged with the
   * player. The model still advances and the dread still rises either way, but
   * no one-shot and no lure fires — the grace period's promise is silence, and
   * a building that keeps hinting while a hound is already on you is just
   * noise over the top of the thing it was hinting at.
   */
  update(w: EntityWorld, live: boolean): void;
  /**
   * Pressure to add this frame, given the baseline the level would have accrued
   * on its own. Negative while the surge is paying back what it lent.
   */
  step(base: number, dt: number): number;
  /** The director spent or withheld its pressure. The loan is written off. */
  forget(): void;
  /**
   * The director just played a noise of its own. Hold the cue track off for a
   * beat so the two never land on top of each other and turn into mush.
   */
  holdCue(): void;
  /**
   * Adopt a freshly spawned agent as the hunter this surge summoned: license it
   * to keep coming from beyond `activeRadius`, and hand it one noise so it has
   * somewhere to start walking.
   *
   * The director calls this and only this; whether a spawn deserves it is the
   * director's decision, since it is the director that knows what it just rolled.
   */
  beginHunt(w: EntityWorld, a: Agent): void;
  /**
   * The window bought a body, but not a narrated hunt: close it anyway.
   *
   * `beginHunt` is the ordinary way a window is spent, and it is only reached
   * when the placement came back long. Every other way a roll can end in a
   * spawn — a level too folded to hold the long band, a kind already carrying a
   * licence — used to leave the window open and still paying `approach.charge`,
   * so the director re-rolled every couple of seconds until it expired. One
   * approach is one thing crossing the floor; this is the other end of that.
   */
  spendWindow(): void;
  /** True while the player is inside a slide's reach and the surge is live. */
  readonly armed: boolean;
  /**
   * True while the approach window is open: the player is plainly walking at a
   * mouth and the building owes them something crossing the floor. The director
   * reads it to force the roll it is about to make into a real hunt, and to bias
   * where that hunt is placed. See `TUNING.director.surge.approach`.
   */
  readonly approaching: boolean;
  /**
   * The mouth the player is closing on, or null when the surge is not armed.
   * The director places an approach hunt against the route to this point, so
   * that what it summons crosses the floor the player is about to walk.
   */
  readonly target: Vec2 | null;
  /** Multiplier on the grace period's decay. 1 when nowhere near a slide. */
  readonly graceScale: number;
  /** The chance the next roll should be a fakeout, given the director's own value. */
  fakeoutChance(base: number): number;
  /** 0..1 dread the audio layer should carry because of the surge. */
  readonly tension: number;
  /** 0..1 escalation before the level's identity scales it. Diagnostic only. */
  readonly intensity: number;
  /** A new run started; the once-per-run line is owed again. */
  newRun(): void;
}

/* ------------------------------------------------------------------ *
 * Implementation
 * ------------------------------------------------------------------ */

class SlideSurgeImpl implements SlideSurge {
  /** Slide mouths on this level, in world space. Empty means the surge is off. */
  private readonly slides: Vec2[] = [];
  /** Its own stream, never the director's: an inactive surge must shift nothing. */
  private rng: RNG = makeRNG(SURGE_SALT);
  private mix = 1;

  /** Seconds of weighted time spent inside the radius, capped at `RAMP_FULL`. */
  private dwell = 0;
  private prox = 0;
  private ramp = 0;
  /** Pressure added beyond the baseline and not yet handed back. */
  private lent = 0;

  private cueTimer = 0;
  private lureTimer = LURE_INTERVAL;
  private trailTimer = 0;
  private isArmed = false;
  /** Per *run*, not per level: the line teaches something once. */
  private warned = false;

  /** The mouth the player is nearest to, and how far off it is. */
  private near: Vec2 | null = null;
  private nearDist = Infinity;
  /**
   * Net metres given up toward that mouth since the surge armed, clamped. This
   * is what separates someone walking to the way down from someone crossing the
   * band on their way to somewhere else; see `approach` in the tuning.
   */
  private closed = 0;
  /** Seconds the approach window has left, and until the next one is allowed. */
  private windowTimer = 0;
  private cooldownTimer = 0;
  /** Closest this hunt has ever been, for the licence renewal in `hunt`. */
  private huntBest = Infinity;

  /** 0..1 closeness of the hunter, if there is one. 0 whenever there is not. */
  private approachLevel = 0;
  /**
   * Whether this hunt has ever been further off than the hand-off distance. See
   * `hunt`: it is what stops a hunter placed round a corner from being treated
   * as having already arrived.
   */
  private huntWasFar = false;
  /**
   * How hard this level hunts, from `spec.threatScale`, clamped. The band is
   * deliberately *not* scaled — the distance is the feature, and the Poolrooms
   * hearing something a long way off is exactly as good there as anywhere. What
   * a calm floor gets is a hunt that is slower to be told where the player is
   * and quicker to run out of licence, so it often peters out on the way.
   */
  private huntScale = 1;
  /**
   * The level's own `threatScale`, clamped away from zero. Everything in this
   * game is scaled by it and the approach is not an exception: it buys the
   * window's pressure and it divides the wait for the next one, so the
   * Poolrooms gets one thing crossing the water when the player finds the way
   * down and Level 2 gets three times as many.
   */
  private threatScale = 1;
  /** Reused by the approach cue; one allocation for the life of the surge. */
  private readonly cuePos: Vec2 = { x: 0, z: 0 };

  constructor(private readonly host: SurgeHost) {}

  get armed(): boolean {
    return this.isArmed;
  }

  get approaching(): boolean {
    return this.windowTimer > 0;
  }

  get target(): Vec2 | null {
    return this.isArmed ? this.near : null;
  }

  get graceScale(): number {
    return 1 + GRACE_BURN * this.prox;
  }

  get intensity(): number {
    return this.prox * (INTENSITY_FLOOR + (1 - INTENSITY_FLOOR) * this.ramp);
  }

  get tension(): number {
    // Two sources, whichever is worse. Standing at the mouth is the first; the
    // second is something crossing the floor toward you, and that one is
    // deliberately not multiplied by proximity to a slide — it has to keep
    // carrying once the player has walked away from the slide and is being
    // followed. `threatOf` in the entity system only starts contributing inside
    // twenty-seven metres, so without this the whole approach would be silent
    // to the audio layer.
    return clamp01(TENSION_CAP * Math.max(this.intensity, this.approachLevel) * this.mix);
  }

  setLevel(level: GeneratedLevel, seed: number): void {
    this.slides.length = 0;
    // The contract with the generator: a slide is an `ExitPoint` and nothing
    // else needs to be told where they are.
    for (const exit of level.exits) {
      if (exit.kind === 'slide') this.slides.push({ x: exit.pos.x, z: exit.pos.z });
    }
    this.rng = makeRNG(seed ^ SURGE_SALT);
    this.mix = audioMix(level.spec.threatScale);
    this.huntScale = Math.min(HUNT_SCALE_MAX, Math.max(HUNT_SCALE_MIN, level.spec.threatScale));
    this.threatScale = Math.min(THREAT_SCALE_MAX, Math.max(THREAT_SCALE_MIN, level.spec.threatScale));
    this.dwell = 0;
    this.prox = 0;
    this.ramp = 0;
    this.lent = 0;
    this.cueTimer = CUE_SLOW;
    this.lureTimer = LURE_INTERVAL;
    this.trailTimer = 0;
    this.approachLevel = 0;
    this.huntWasFar = false;
    this.isArmed = false;
    this.near = null;
    this.nearDist = Infinity;
    this.closed = 0;
    this.windowTimer = 0;
    this.cooldownTimer = 0;
    this.huntBest = Infinity;
  }

  newRun(): void {
    this.warned = false;
  }

  update(w: EntityWorld, live: boolean): void {
    const dt = w.ctx.dt;
    if (this.slides.length === 0) return;

    const was = this.nearDist;
    const d = this.nearestSlide(w.player.pos);
    const wasArmed = this.isArmed;
    // Hysteresis on the reach, because arming is what decides whether the next
    // spawn is narrated as a hunt: a player walking the edge of the band must
    // not flip that on and off every few steps.
    this.isArmed = d <= (wasArmed ? ARM_RADIUS + ARM_HYSTERESIS : ARM_RADIUS);

    // Escalation is a mouth mechanic and keeps its own, much smaller radius.
    // Everything the reach buys is the approach; everything proximity buys is
    // the price of standing at the way down and thinking about it.
    const prox = smoothstep(DWELL_RADIUS, MOUTH_RADIUS, d);

    // Weighted by proximity: hovering at the edge of the room escalates, but
    // slowly. Standing at the mouth is what actually runs the clock.
    if (prox > 0) this.dwell = Math.min(RAMP_FULL, this.dwell + dt * prox);
    else this.dwell = Math.max(0, this.dwell - dt * DWELL_DECAY);

    this.prox = prox;
    this.ramp = this.dwell / RAMP_FULL;

    const hunter = this.hunter();
    this.approachLevel = hunter ? smoothstep(HUNT_FAR, HUNT_NEAR, hunter.state.distToPlayer) : 0;
    this.trackApproach(dt, was, d, live, hunter !== null);

    if (!this.isArmed && !hunter) return;

    if (this.isArmed) {
      // Crossing in: the first cue lands within a few seconds, so even a player
      // who walks up, looks and jumps straight in hears the building react.
      if (!wasArmed) this.cueTimer = Math.min(this.cueTimer, this.rng.range(2.0, 3.5));

      // The surge owns *when* — it is the only thing that knows the intensity —
      // and `game.ts` owns *what*, alongside the game's other once-per-run
      // teaching lines. See `RECALL` in `survival/notes.ts`.
      if (!this.warned && this.intensity > WARN_INTENSITY) {
        this.warned = true;
        w.events.emit('surge:warn', {});
      }
    }

    // A hunt narrates itself whether or not the director is accruing. `live`
    // goes false the moment anything starts investigating, and the hunter
    // starts investigating on its first think — so gating the approach on it
    // would silence the one thing the player most needs to hear. It also takes
    // the cue track over outright: one noise source at a time, and while
    // something is genuinely coming, the room's idle hints are not it.
    if (hunter) {
      this.hunt(w, hunter, dt);
      return;
    }

    if (!this.isArmed || !live) return;

    this.cueTimer -= dt;
    if (this.cueTimer <= 0) {
      this.cueTimer = lerp(CUE_SLOW, CUE_FAST, clamp01(this.intensity * this.mix)) * this.rng.range(0.85, 1.15);
      this.cue(w);
    }

    this.lureTimer -= dt;
    if (this.lureTimer <= 0) {
      this.lureTimer = LURE_INTERVAL;
      if (this.intensity >= LURE_INTENSITY) this.lure(w);
    }
  }

  step(base: number, dt: number): number {
    const scale = 1 + this.prox * (SURGE_BASE + SURGE_PEAK * this.ramp);
    // The window is a lump sum rather than a multiplier, because a multiplier
    // of a rate cannot promise a spawn inside a walk that lasts twenty seconds
    // — and the promise is the whole feature. It is lent like everything else
    // here, so a window that buys nothing is handed straight back.
    const extra = base * (scale - 1) + (this.windowTimer > 0 ? APPROACH_CHARGE * dt * this.threatScale : 0);
    if (extra > 0) {
      this.lent += extra;
      return base + extra;
    }
    if (this.lent <= 0) return base;
    // Out of range. Give back what was borrowed, or the next room the player
    // walks into inherits a director charged by a slide they never took.
    const back = Math.min(this.lent, DRAIN_RATE * dt);
    this.lent -= back;
    return base - back;
  }

  forget(): void {
    this.lent = 0;
  }

  holdCue(): void {
    this.cueTimer = Math.max(this.cueTimer, CUE_AFTER_FAKEOUT);
  }

  fakeoutChance(base: number): number {
    // The one roll in the game that is not allowed to be a scare. Everything
    // else, including the extra rolls lingering at the mouth buys, still runs
    // at the ordinary fakeout-heavy odds.
    if (this.windowTimer > 0) return APPROACH_FAKEOUT;
    if (this.prox <= 0) return base;
    return lerp(base, lerp(FAKEOUT_EARLY, FAKEOUT_LATE, this.ramp), this.prox);
  }

  /* ---------------------------------------------------------------- *
   * Outputs
   * ---------------------------------------------------------------- */

  /**
   * A noise from the dark, closer each time. This is the announcement: the
   * player is meant to hear the room shrinking and understand that it started
   * when they walked up to the slide.
   */
  private cue(w: EntityWorld): void {
    const i = this.intensity;
    const r = lerp(CUE_FAR, CUE_NEAR, i);
    const spot = this.host.offscreenSpot(w, r * 0.65, r, false) ?? this.host.offscreenSpot(w, 6, CUE_FAR, false);
    if (!spot) return;
    const pool = i > CUE_VOICE_INTENSITY ? SURGE_SFX_CLOSE : SURGE_SFX;
    w.audio.play(this.rng.pick(pool), {
      pos: spot,
      gain: lerp(0.45, 1.0, i) * (0.5 + 0.5 * this.mix),
      // Pitched down as it closes: the same sound, heavier.
      rate: lerp(1.05, 0.8, i),
    });
  }

  /**
   * Point whatever is already awake at the player.
   *
   * Written the same way `EntitySystem.hearNoise` writes it — a position and a
   * loudness, consumed by the agent's next think tick — so nothing here can
   * move a body, and a hound handed this still has to find the player the hard
   * way. It is the building shouting, not a teleport.
   */
  private lure(w: EntityWorld): void {
    for (const a of this.host.agents) {
      if (a.asleep || a.def.walkSpeed <= 0 || a.def.hearingRange <= 0) continue;
      // Never quieter than something it has already been told about.
      if (a.heardPending && a.heardLoudness >= LURE_LOUDNESS) continue;
      a.heardPos.x = w.player.pos.x;
      a.heardPos.z = w.player.pos.z;
      a.heardLoudness = LURE_LOUDNESS;
      a.heardPending = true;
    }
  }

  /* ---------------------------------------------------------------- *
   * The long hunt
   * ---------------------------------------------------------------- */

  spendWindow(): void {
    // Only a window that is actually open can be spent. Without the guard this
    // would start the full cooldown for a spawn the approach never paid for.
    if (this.windowTimer <= 0) return;
    this.closeWindow();
  }

  /** Shut the window and start the wait for the next one. */
  private closeWindow(): void {
    this.windowTimer = 0;
    this.closed = 0;
    this.cooldownTimer = APPROACH_COOLDOWN / this.threatScale;
  }

  beginHunt(w: EntityWorld, a: Agent): void {
    a.longHunt = TUNING.entities.surgeSpawn.duration * this.huntScale;
    // The window has been spent. Whether or not this hunt ever reaches the
    // player, the building has said its piece about this slide for a while.
    this.closeWindow();
    this.huntBest = Infinity;
    // The flag is the identity now that ordinary chasers carry licences too;
    // see `Agent.surgeHunt`. Set with the licence and cleared with it.
    a.surgeHunt = true;
    // The approach announces itself almost at once. A player should hear it
    // start rather than discover it already halfway across the floor.
    this.cueTimer = this.rng.range(1.0, 2.2);
    this.trailTimer = this.trailInterval(w);
    // A fresh hunt has not been anywhere yet; see `hunt`.
    this.huntWasFar = false;
    // One noise, near the player, so it has somewhere to start walking. Written
    // exactly the way `lure` writes one — a position and a loudness for the next
    // think tick — so the thing still has to cover the ground on foot, and can
    // still lose the thread the moment the player stops giving it new ones.
    this.handOff(w, a, HUNT_WAKE_LOUDNESS);
  }

  /**
   * The hunter this surge summoned, or null. The flag *is* the identity: no
   * reference is stored, so an agent that gets despawned mid-approach cannot
   * leave a dangling one behind, and a flag cleared with the licence ends the
   * hunt with nothing else to clean up. The list holds nought to three entities.
   *
   * `longHunt` alone was the identity until the pursuit licence started being
   * granted to any ordinary hunter that commits. Without the flag as well, a
   * hound that picked the player up on its own two rooms away would be adopted
   * as this surge's summoned hunter, and the approach cue track would start
   * announcing something that had already arrived.
   */
  private hunter(): Agent | null {
    for (const a of this.host.agents) if (a.surgeHunt && a.longHunt > 0) return a;
    return null;
  }

  /** One frame of an approach: make it audible, and keep it pointed. */
  private hunt(w: EntityWorld, a: Agent, dt: number): void {
    // Inside the handover the hunter's own growls, footfalls and ears are what
    // the encounter runs on, and the building stops helping entirely. That is
    // what keeps the counterplay real: the last thirty metres have to be earned
    // by hearing the player, so going quiet still loses it.
    //
    // The distance here is the straight line, because what it is really asking
    // is whether the thing can be heard from where it stands — and sound does
    // not walk round corners. The placement band is metres of *walking*, so on
    // a folded level a hunter with seventy metres to cover can be twenty metres
    // away through a wall on its first frame. Handing over there would stand
    // the cue track down before the approach had begun and leave the whole walk
    // silent, so the hand-off waits until the hunter has either been seen
    // properly far off at least once, or has got close enough to be making its
    // own noise. Both are true within a frame or two of an ordinary hunt; only
    // the folded case notices the difference.
    const d = a.state.distToPlayer;
    if (d > HUNT_HANDOVER) this.huntWasFar = true;
    const m = a.state.mode;
    const audibleItself = m === 'stalk' || m === 'chase' || m === 'attack';
    const close = d <= HUNT_HANDOVER && (this.huntWasFar || audibleItself);

    // A hunt that is still closing keeps its licence. `surgeSpawn.duration` is
    // one fixed budget for every approach, and on a level that scales it down
    // it is shorter than the walk it paid for — so the sleep rule cancelled
    // hunts halfway across the floor. Renewing on ground gained fixes that
    // without lengthening a hunt that has stopped making any: nothing is topped
    // up while it mills about, and `progressLicence` after it stalls it sleeps.
    if (d < this.huntBest - HUNT_PROGRESS_STEP) {
      this.huntBest = d;
      a.longHunt = Math.max(a.longHunt, HUNT_PROGRESS_LICENCE);
    }

    this.cueTimer -= dt;
    if (this.cueTimer <= 0) {
      this.cueTimer = lerp(HUNT_CUE_SLOW, HUNT_CUE_FAST, this.approachLevel) * this.rng.range(0.85, 1.15);
      if (!close) this.huntCue(w, a);
    }

    // A hunter that has walked to where it was told, found an empty room and
    // gone back to casting about is told nothing more until its interval comes
    // round, and then walks to another address the player left seven seconds
    // ago. That stall is most of why a hound took over a minute to cross fifty
    // metres. The building has not gone quiet just because the last address was
    // wrong, so a hunt that has lost the thread gets the next one sooner —
    // still only if the player is making a noise worth passing on, which is
    // checked in `trail` itself and is the whole of the counterplay.
    if (a.state.mode === 'wander' || a.state.mode === 'dormant') {
      this.trailTimer = Math.min(this.trailTimer, TRAIL_FAST);
    }

    // The trail runs closer in than the cue track does, and the two hand over
    // for different reasons: the cue stands down where the hunter's own growls
    // and footfalls become the sound of it, and the trail stands down where the
    // hunter's own ears can finish the job. The second of those is the shorter
    // distance — see `hunt.trailHandover` — and treating them as one number was
    // why most approaches ended in an empty room.
    this.trailTimer -= dt;
    if (this.trailTimer <= 0) {
      this.trailTimer = this.trailInterval(w);
      if (d > HUNT_TRAIL_HANDOVER) this.trail(w, a);
    }
  }

  /**
   * The approach, as sound. A one-shot on the hunter's true bearing, arriving
   * faster, louder and lower as it closes.
   *
   * The bearing is honest because that is the part the player acts on — which
   * way is it coming from, and is the slide that way or the other way. The
   * distance is compressed: `audio.maxAudibleDistance` is forty-two metres, so
   * a sound played where the hunter actually stands would be silence for most
   * of an approach. The compression is monotone and never places the noise
   * further out than the hunter really is, so closer always sounds closer.
   */
  private huntCue(w: EntityWorld, a: Agent): void {
    const dx = a.state.pos.x - w.player.pos.x;
    const dz = a.state.pos.z - w.player.pos.z;
    const d = Math.hypot(dx, dz);
    if (d < 1e-3) return;

    const t = clamp01((d - HUNT_NEAR) / (HUNT_FAR - HUNT_NEAR));
    const r = Math.min(d, lerp(HUNT_TRACK_NEAR, HUNT_TRACK_FAR, t));
    this.cuePos.x = w.player.pos.x + (dx / d) * r;
    this.cuePos.z = w.player.pos.z + (dz / d) * r;

    const i = this.approachLevel;
    const pool = i > CUE_VOICE_INTENSITY ? SURGE_SFX_CLOSE : SURGE_SFX;
    w.audio.play(this.rng.pick(pool), {
      pos: this.cuePos,
      gain: lerp(0.5, 1.0, i) * (0.5 + 0.5 * this.mix),
      // Pitched down as it closes: the same sound, heavier.
      rate: lerp(1.05, 0.78, i),
    });
  }

  /**
   * Keep the hunter pointed at the player — but only while the player is making
   * a noise worth passing on.
   *
   * This is the whole counterplay, and it is the game's own line rather than a
   * new one: below `entities.noiseThreshold` nothing in the building hears you,
   * and a crouched player emits 0.08 against a threshold of 0.22. Go quiet and
   * the trail simply stops; the hunter walks to where you last were, casts
   * about for `searchDuration` seconds and gives up. Sprint and it is refreshed
   * more than twice as often.
   */
  private trail(w: EntityWorld, a: Agent): void {
    const p = w.player;
    if (p.hidden || p.noise < TUNING.entities.noiseThreshold) return;
    this.handOff(w, a, p.noise);
  }

  /** Seconds until the next trail update, from how loud the player is being. */
  private trailInterval(w: EntityWorld): number {
    return lerp(TRAIL_SLOW, TRAIL_FAST, clamp01(w.player.noise)) / this.huntScale;
  }

  /**
   * Write a heard position onto the hunter, blurred by how far away it still
   * is. The building knows which room; the hunter's own ears have to do the
   * rest. Without the blur this would be a homing beacon and standing in the
   * dark would stop being an answer.
   */
  private handOff(w: EntityWorld, a: Agent, loudness: number): void {
    // Never quieter than something it has already been told about.
    if (a.heardPending && a.heardLoudness >= loudness) return;
    const p = w.player;
    const spread = TRAIL_SPREAD * (1 - this.approachLevel);
    const spot = spread > 1 ? randomWalkableNear(w.grid, this.rng, p.pos, spread * 0.45, spread, 6) : null;
    a.heardPos.x = spot ? spot.x : p.pos.x;
    a.heardPos.z = spot ? spot.z : p.pos.z;
    a.heardLoudness = loudness;
    a.heardPending = true;
  }

  /**
   * Metres to the nearest slide mouth, remembering which one it was. Levels
   * carry a handful at most.
   */
  private nearestSlide(pos: Vec2): number {
    let best = Infinity;
    let which: Vec2 | null = null;
    for (const s of this.slides) {
      const d = Math.hypot(s.x - pos.x, s.z - pos.z);
      if (d < best) {
        best = d;
        which = s;
      }
    }
    this.near = which;
    this.nearDist = best;
    return best;
  }

  /**
   * Decide whether the player is *going* to a slide, and open the window if
   * they are.
   *
   * The test is ground given up rather than a bearing, because a bearing is a
   * frame of a walk and this is a decision about a journey: somebody crossing
   * the band on the way past never nets `APPROACH_COMMIT` metres of closing,
   * and somebody who turns round and leaves hands `APPROACH_SLACK` of it back
   * and the building stops believing them.
   *
   * `live` is the director's own accrual state, so nothing opens during the
   * grace period or on top of an encounter that is already happening — the
   * window buys a hunter, and a second thing crossing the floor while the first
   * one is on the player is noise rather than dread.
   */
  private trackApproach(dt: number, was: number, now: number, live: boolean, hunting: boolean): void {
    if (this.cooldownTimer > 0) this.cooldownTimer = Math.max(0, this.cooldownTimer - dt);

    if (now > APPROACH_WATCH) {
      this.closed = 0;
      this.windowTimer = 0;
      return;
    }

    if (Number.isFinite(was)) {
      // Clamped both ends: the credit for an approach cannot be banked far in
      // advance, and one doorway's worth of walking the wrong way cannot cancel
      // an approach that is plainly happening.
      this.closed = Math.min(APPROACH_COMMIT, Math.max(-APPROACH_SLACK, this.closed + (was - now)));
    }

    if (this.windowTimer > 0) {
      this.windowTimer -= dt;
      // It bought nothing — no room on the floor, or the director was busy by
      // the time it rolled. Wait a while and offer again rather than give up on
      // this slide for two minutes.
      if (this.windowTimer <= 0) this.cooldownTimer = (APPROACH_COOLDOWN * APPROACH_RETRY) / this.threatScale;
      return;
    }

    if (!this.isArmed || !live || hunting || this.cooldownTimer > 0) return;
    if (this.closed < APPROACH_COMMIT) return;
    this.windowTimer = APPROACH_WINDOW;
    this.closed = 0;
  }
}

/** Build the slide surge for a director. */
export function makeSlideSurge(host: SurgeHost): SlideSurge {
  return new SlideSurgeImpl(host);
}
